// src/components/cognition/tables/raw-attribute-extraction-table.tsx
import React from "react";

function normKey(s: string) {
  return String(s || "")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");
}

function pickValue(row: any, modelId: string, modelLabel: (id: string) => string) {
  const values = row?.values && typeof row.values === "object" ? row.values : null;

  if (values) {
    const direct = [
      modelId,
      modelId.toLowerCase(),
      modelId.toUpperCase(),
      modelLabel(modelId),
      modelLabel(modelId).toLowerCase(),
    ];

    for (const k of direct) {
      const v = values[k];
      if (v != null && String(v).trim() !== "") return v;
    }

    const target = normKey(modelId);
    const targetLabel = normKey(modelLabel(modelId));

    for (const k of Object.keys(values)) {
      const nk = normKey(k);
      if (nk.includes(target) || nk.includes(targetLabel)) {
        const v = values[k];
        if (v != null && String(v).trim() !== "") return v;
      }
    }
  }

  const candidates = [
    modelId,
    modelId.toLowerCase(),
    modelLabel(modelId),
    modelLabel(modelId).toLowerCase(),
  ];
  for (const k of candidates) {
    if (row && k in row) {
      const v = row[k];
      if (v != null && String(v).trim() !== "") return v;
    }
  }

  return "—";
}

function deriveConsensus(row: any, totalModels: number) {
  const raw = String(row?.consensus || "").trim();
  if (raw) return raw; // legacy support

  const strong = row?.strong === true;
  const modelsWithValue = Number(row?.modelsWithValue ?? row?.models_with_value ?? 0);
  const uniqueValues = Number(row?.uniqueValues ?? row?.unique_values ?? 0);

  if (strong || (modelsWithValue === totalModels && uniqueValues <= 1)) return "strong";

  if (uniqueValues >= 2) return "divergent";

  if (modelsWithValue < totalModels) return "weak";

  // fallback
  return "moderate";
}

export function RawAttributeExtractionTable({
  rows,
  modelIds,
  modelLabel,
  renderConsensus,
}: {
  rows: any[];
  modelIds: string[];
  modelLabel: (id: string) => string;
  renderConsensus: (c: string) => React.ReactNode;
}) {
  if (!rows?.length) return null;

  const totalModels = modelIds?.length || 1;

  return (
    <div className="overflow-x-auto">
      <table className="w-full" style={{ borderCollapse: "separate", borderSpacing: 0 }}>
        <thead>
          <tr>
            <th
              className="px-4 py-3 text-left"
              style={{
                backgroundColor: "var(--alpha-teal-10)",
                borderTop: "1px solid var(--border)",
                borderLeft: "1px solid var(--border)",
                borderTopLeftRadius: "var(--radius)",
                fontWeight: "var(--font-weight-semibold)",
              }}
            >
              Attribute
            </th>

            {modelIds.map((id) => (
              <th
                key={id}
                className="px-4 py-3 text-center"
                style={{
                  backgroundColor: "var(--alpha-teal-10)",
                  borderTop: "1px solid var(--border)",
                  borderLeft: "1px solid var(--border)",
                  fontWeight: "var(--font-weight-semibold)",
                }}
              >
                {modelLabel(id)}
              </th>
            ))}

            <th
              className="px-4 py-3 text-center"
              style={{
                backgroundColor: "var(--alpha-teal-10)",
                borderTop: "1px solid var(--border)",
                borderLeft: "1px solid var(--border)",
                borderRight: "1px solid var(--border)",
                borderTopRightRadius: "var(--radius)",
                fontWeight: "var(--font-weight-semibold)",
              }}
            >
              Consensus
            </th>
          </tr>
        </thead>

        <tbody>
          {rows.map((row: any, idx: number) => {
            const consensus = deriveConsensus(row, totalModels);

            return (
              <tr key={`${row.attribute}-${idx}`}>
                <td
                  className="px-4 py-3"
                  style={{
                    borderLeft: "1px solid var(--border)",
                    borderTop: "1px solid var(--border)",
                    fontWeight: "var(--font-weight-medium)",
                  }}
                >
                  {row.attribute}
                </td>

                {modelIds.map((id) => (
                  <td
                    key={id}
                    className="px-4 py-3 text-center"
                    style={{
                      borderLeft: "1px solid var(--border)",
                      borderTop: "1px solid var(--border)",
                      color: "var(--foreground)",
                    }}
                  >
                    {String(pickValue(row, id, modelLabel))}
                  </td>
                ))}

                <td
                  className="px-4 py-3 text-center"
                  style={{
                    borderLeft: "1px solid var(--border)",
                    borderTop: "1px solid var(--border)",
                    borderRight: "1px solid var(--border)",
                  }}
                >
                  <div className="flex justify-center">{renderConsensus(consensus)}</div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
